/**
 * Integración con WordPress para Stand Reservation App
 * Este archivo maneja toda la comunicación entre la app React y WordPress
 */

// ========================================
// TIPOS PARA LA COMUNICACIÓN CON WORDPRESS
// ========================================

export interface WordPressFormData {
  name: string;
  lastname: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  job_title: string;
  area: string;
  subarea: string;
  organization_type: string;
  industries: string;
  message: string;
  stands: string;
  autorizo_whatsapp: boolean;
  deseo_novedades: boolean;
}

export interface HeightChangeMessage {
  type: 'iframe-height-change';
  height: number;
}

export interface FormSubmissionMessage {
  type: 'stand-form-submission';
  formData: WordPressFormData;
  action?: 'replace' | 'add' | 'new';
  previousQueryId?: string;
}

export interface IframeReadyMessage {
  type: 'iframe-ready';
  height: number;
}

// ========================================
// FUNCIONES DE COMUNICACIÓN
// ========================================

/**
 * Envía un mensaje al iframe padre (WordPress)
 */
export const sendMessageToParent = (message: any) => {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, '*');
  }
};

/**
 * Envía datos del formulario a WordPress
 */
export const submitFormToWordPress = (
  formData: WordPressFormData, 
  action: 'replace' | 'add' | 'new' = 'new',
  previousQueryId?: string
) => {
  const message: FormSubmissionMessage = {
    type: 'stand-form-submission',
    formData: formData,
    action: action,
    previousQueryId: previousQueryId
  };
  
  sendMessageToParent(message);
};

const MIN_IFRAME_HEIGHT = 600;

const getContentHeight = (): number => {
  const bodyHeight = Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight
  );
  const documentHeight = Math.max(
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight,
    document.documentElement.clientHeight
  );
  return Math.max(bodyHeight, documentHeight, MIN_IFRAME_HEIGHT) + 20;
};

// Variable para almacenar la última altura notificada
let lastNotifiedHeight = 0;

/**
 * Notifica cambios de altura del iframe
 */
export const notifyHeightChange = () => {
  // Usar requestAnimationFrame para asegurar que el DOM esté actualizado
  requestAnimationFrame(() => {
    // Calcular altura de manera más precisa
    const height = getContentHeight();
    const heightDifference = Math.abs(height - lastNotifiedHeight);
    if (heightDifference > 5) {
      lastNotifiedHeight = height;
      
      const message: HeightChangeMessage = {
        type: 'iframe-height-change',
        height: height
      };
      
      sendMessageToParent(message);
    }
  });
};

/**
 * Notifica que el iframe está listo
 */
export const notifyIframeReady = () => {
  const height = getContentHeight();
  lastNotifiedHeight = height;

  const message: IframeReadyMessage = {
    type: 'iframe-ready',
    height: height
  };
  
  sendMessageToParent(message);
};

// ========================================
// FUNCIONES DE UTILIDAD
// ========================================

/**
 * Mapea los datos del formulario React al formato de WordPress
 */
export const mapFormDataToWordPress = (
  reactFormData: any,
  selectedStands: any[]
): WordPressFormData => {
  // Convertir stands a string con solo nombres separados por coma
  const standsString = selectedStands.map(stand => stand.name).join(', ');
  
  return {
    name: reactFormData.nombre || '',
    lastname: reactFormData.apellido || '',
    email: reactFormData.email,
    phone: reactFormData.telefono,
    company: reactFormData.organizacion,
    website: reactFormData.sitioWeb || '',
    job_title: reactFormData.cargo || '',
    area: reactFormData.area || '',
    subarea: reactFormData.subarea || '',
    organization_type: reactFormData.tipoOrganizacion || '',
    industries: reactFormData.industrias ? reactFormData.industrias.join(', ') : '',
    message: reactFormData.mensaje || '',
    stands: standsString,
    autorizo_whatsapp: Boolean(reactFormData.autorizoWhatsApp),
    deseo_novedades: Boolean(reactFormData.deseoNovedades)
  };
};

/**
 * Configura los listeners para la comunicación con WordPress
 */
export const setupWordPressIntegration = (
  onIframeReady?: (height: number) => void
) => {
  // Listener para mensajes del iframe padre
  const handleMessage = (event: MessageEvent) => {
    if (event.data?.type === 'request-height') {
      notifyHeightChange();
      notifyIframeReady();
      return;
    }
    if (event.data?.type === 'iframe-ready' && onIframeReady) {
      onIframeReady(event.data.height);
    }
  };
  
  window.addEventListener('message', handleMessage);
  
  notifyIframeReady();
  setTimeout(() => notifyIframeReady(), 1000);
  setTimeout(() => notifyIframeReady(), 2000);
  setTimeout(() => notifyIframeReady(), 4000);
  
  // Observer para detectar cambios en el DOM
  const observer = new MutationObserver(() => {
    notifyHeightChange();
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true
  });
  
  // Listener para cambios de orientación en mobile
  const handleOrientationChange = () => {
    setTimeout(() => {
      notifyHeightChange();
    }, 100);
  };
  
  window.addEventListener('orientationchange', handleOrientationChange);
  window.addEventListener('resize', handleOrientationChange);
  
  // Cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
    window.removeEventListener('orientationchange', handleOrientationChange);
    window.removeEventListener('resize', handleOrientationChange);
    observer.disconnect();
  };
};

/**
 * Función para mostrar notificaciones (opcional)
 */
export const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
  // Crear elemento de notificación
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 15px 20px;
    border-radius: 5px;
    color: white;
    font-weight: bold;
    z-index: 9999;
    max-width: 300px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    background: ${type === 'success' ? '#4CAF50' : '#f44336'};
    font-family: Arial, sans-serif;
  `;
  notification.textContent = message;
  
  document.body.appendChild(notification);
  
  // Auto-remover después de 5 segundos
  setTimeout(() => {
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }, 5000);
};

// ========================================
// EXPORTAR FUNCIONES PRINCIPALES
// ========================================

const wordpressIntegration = {
  submitFormToWordPress,
  notifyHeightChange,
  notifyIframeReady,
  mapFormDataToWordPress,
  setupWordPressIntegration,
  showNotification
};

export default wordpressIntegration;
